import {
  Box,
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import React, { useCallback, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ListRequestOfBaseFilter,
  ListResponseOfSkuResource,
  SkuResource,
  SkuResourceSequenceRequest,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import SkuResourceForm from "./SkuResourceForm";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DropResult } from "react-beautiful-dnd";
import { FORM_ERROR } from "final-form";
import translateError from "utils/translateError";
import { useStrictQuery } from "hooks/useStrictQuery";
import { withErrorBoundary } from "hocs/withErrorBoundary";

interface SkuResourceWithId extends SkuResource {
  id: string;
}

const SkuResources = (props: SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [deleting, setDeleting] = useState<SkuResource | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);

  const DeleteButton = ({ value }: { value: SkuResource }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          onClick={() => {
            setDeleting(value);
            deleteOpenActions.setTrue();
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const handleDelete = useCallback(() => {
    const id = deleting?.id;
    setDeleting(null);
    if (id)
      return registerPromise(skuService.deleteSkuResource(id))
        .then(() => {
          queryClient.invalidateQueries(["skuResources", props.sku.id!]);

          setSnackbarMessage(
            new SnackbarMessageProps(
              t({ id: "skuFacultyDeleted" }),
              Severity.Warning
            )
          );

          props.onSkuUpdated && props.onSkuUpdated(props.sku.id!);
          deleteOpenActions.setFalse();
        })
        .catch((err: any) => {
          console.log(err);
        });
  }, [
    deleteOpenActions,
    deleting?.id,
    props,
    queryClient,
    registerPromise,
    skuService,
    t,
  ]);

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuResource>(
    ["getSkuResources", props.sku.id!],
    () =>
      skuService.getSkuResources(
        props.sku.id!,
        ListRequestOfBaseFilter.fromJS({
          sortField: "sequence",
        })
      )
  );

  const skuResources = useMemo(
    () => (data?.data ?? []) as SkuResourceWithId[],
    [data?.data]
  );

  const onDragEndHandler = async (result: DropResult) => {
    try {
      if (!result.destination) {
        return;
      }
      await skuService.saveSkuResourceSequence(
        props.sku.id!,
        new SkuResourceSequenceRequest({
          skuResourceId: result.draggableId,
          sequence: result.destination?.index,
        })
      );
      queryClient.invalidateQueries(["skuResources", props.sku.id!]);
    } catch (error) {
      console.log(error);
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DragHandleIcon,
      label: "",
      noSort: true,
    } as TableCellType,
    {
      accessor: "resource.value",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuFacultyMemberLabel" }),
    },
    {
      accessor: "resourceType.value",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuFacultyTypeLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  const onResourceCreated = () => {
    queryClient.invalidateQueries(["skuResources", props.sku.id!]);
    createOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "skuFacultyCreated" }))
    );
  };

  return (
    <Container>
      <Box mb={3}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box textAlign="right">
              <Button
                variant="outlined"
                type="submit"
                color="primary"
                onClick={createOpenActions.setTrue}
                data-cy="addResourceButton"
              >
                <T id="skuFacultyCreateButtonLabel" />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SortableTable
        {...{
          rows: skuResources,
          loading: isFetching,

          dense: true,
          title: t({ id: "skuFacultyTitle" }),
          cells,
          allowDragAndDrop: true,
          onDragAndDropEnd: onDragEndHandler,
        }}
      />

      <DeleteEntityDialog
        dialogTitleKey="skuFacultyDeleteTitle"
        contentKey="skuFacultyDeleteQuestion"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuResourceForm
          {...props}
          setSnackbarMessage={setSnackbarMessage}
          skuResources={skuResources}
          onResourceCreated={onResourceCreated}
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default withErrorBoundary(SkuResources);
